import { defineStore } from 'pinia'
import { computed } from 'vue'
import { useNuxtApp } from '#app'
import { useSyncData } from '@/composables/useSyncData'
import { Employee } from '@/_api/models/employee'
import { useAuthStore } from '@/stores/useAuthStore'

export const useEmployeeStore = defineStore('employee', () => {
  const { $api, $grants } = useNuxtApp()
  const authStore = useAuthStore()

  const accountId = computed(() => authStore.activeGuid)

  const {
    items: employees,
    count,
    hydrated,
    syncState,
    isSyncing,
    syncProgress,
    sync: _sync,
    findById,
  } = useSyncData<Employee>({
    service: $api.employee,
    key: 'employees',
    accountId,
    grants: { employee: ['view'] },
  })

  const sync = async (options?: { force?: boolean; change?: boolean }) => {
    if ($grants && !$grants.all({ employee: ['view'] })) {
      return
    }
    await _sync(options)
  }

  const syncDate = computed(() => syncState.value.date)

  const all = computed(() => [...employees.value].sort((left, right) => Employee.compare(left, right) ?? 0))

  // Delegate to the Map-backed findById in useSyncData — O(1) instead of
  // O(n). See .docs/performance.md.
  const getEmployeeByID = (idEmployee: number | string) => findById(idEmployee)

  const employeesTypeEmployee = computed(() =>
    all.value.filter((e: Employee) => e.isEmployeeType)
  )

  const employeesTypeResource = computed(() =>
    all.value.filter((e: Employee) => e.isResourceType)
  )

  const employeesForService = (service: any) => {
    return all.value.filter((e: Employee) =>
      service?.employeeIDs?.includes(e.idEmployee)
    )
  }

  const employeesForResource = (serviceResource: any) => {
    return all.value.filter((e: Employee) =>
      serviceResource?.resourceIDs?.includes(e.idEmployee)
    )
  }

  return {
    employees,
    count,
    hydrated,
    syncState,
    isSyncing,
    syncProgress,
    syncDate,
    sync,
    findById,
    all,
    getEmployeeByID,
    employeesTypeEmployee,
    employeesTypeResource,
    employeesForService,
    employeesForResource,
  }
})
